/**
 * Composite Q+V score — EUCLIDEAN distance from origin in (Q, V) space, with Q ≥ 0 filter.
 *
 * 9-year multi-period backtest (2017-2025 rebalances, top-10 annual, 1yr forward return):
 *   sqrt(Q²+V²) with Q≥0 filter:    avg 40.7%, Sharpe 1.96, worst +3.2%, 100% hit rate  ← ADOPTED
 *   sqrt(Q²+V²) no filter:          avg 41.1%, Sharpe 1.77, worst +0.9%, 100% hit rate
 *   Q+V linear 50/50:               avg 36.0%, Sharpe 1.49
 *   sqrt((3+Q)²+(3+V)²) shifted:    avg 36.1%, Sharpe 1.45, worst -1.5%
 *   sqrt(Q²+V²) with Q≥0 AND V≥0:   avg 28.6%, Sharpe 1.12, worst -11.8%  ← tested 2026-04-23 evening, rejected
 *
 * Why this wins: the Q≥0 filter excludes truly poor-quality stocks (prevents cheap-junk traps);
 * Euclidean rewards extremity on at least one axis (PLTR-style expensive-quality CAN qualify, but
 * only when the market rewards it — the backtest shows this regime-adapts). Adding V≥0 looks
 * value-disciplined on paper but empirically destroyed Sharpe by forcing exits during momentum
 * regimes (Mag-7, AI, 2024-2026).
 *
 * Backtest note: trailing factors only (no point-in-time Bloomberg forward consensus in the cache).
 * Selection-rule shape should compare validly because forward factors would shift Q/V uniformly.
 */

/** Minimum composite score (post-asymmetric formula) for a stock to be
 *  Alpha Zone eligible. 1.5 = BUY tier per lib/composite-tiers.ts.
 *  Replaces the prior Q-only gate (Q >= 0) on 2026-05-14: the composite
 *  is the central measure of the Alpha Zone, so the admission rule
 *  matches the published tier vocabulary. STRONG BUY ≥ 3.0, BUY ≥ 1.5,
 *  HOLD ≥ 0, SELL < 0 → admit only BUY+.
 *
 *  Backtest cost vs prior Q≥0 rule: Sharpe 1.10 → 1.06 across 128 monthly
 *  vintages — within noise. The trade is intellectual coherence: the rule
 *  matches the display tier, prospective viewers see only BUY-rated names
 *  in the basket, no separate Q-gate to explain. */
export const ALPHA_ZONE_COMPOSITE_MIN = 1.5;

// ASYMMETRIC COMPOSITE — high quality + cheap rewards geometrically, low
// quality + expensive penalize linearly. Asymmetric on BOTH axes.
//
// Formula:  score = sqrt(max(Q,0)² + max(βV,0)²) + min(Q,0) + min(βV,0)
//   Q ≥ 0, βV ≥ 0:  sqrt(Q² + (βV)²)   — full Euclidean reward
//   Q < 0, βV ≥ 0:  sqrt(0 + (βV)²) + Q  =  βV + Q
//   Q ≥ 0, βV < 0:  Q + βV
//   Q < 0, βV < 0:  Q + βV
//
// Properties: continuous, monotonic in both Q and βV, smooth at Q=0 and βV=0.
// Range: [-6, +6.71] approx.
//
// Corners (Q, V clamped to [-3, +3], β=1.25):
//   (Q=+3, V=+3)  best:               sqrt(9 + 14.06) = 4.81
//   (Q=+3, V=0)   high Q, fair V:     3.00
//   (Q=+3, V=-3)  pay up for quality: 3 + (-3.75) = -0.75
//   (Q=0,  V=+3)  pure cheap:         sqrt(0 + 14.06) = 3.75
//   (Q=-3, V=+3)  cheap junk:         3.75 + (-3) = 0.75   (was 4.81 — bug)
//   (Q=-3, V=-3)  worst:              -3 + (-3.75) = -6.75
//
// History — the Q<0 fix landed 2026-04-30 after Steve flagged that BG (Q=-2.48,
// V=+0.84) was scoring 2.69 (rank ~10) under the older formula because (-2.48)²
// erased the sign and contributed +6.15 under the square root. Under the
// corrected formula BG scores ≈ -1.43 (sell tier) — the right answer.
//
// β (Valuation weight coefficient) = 1.25 — selected by grid optimizer +
// walk-forward CV on the 9-yr cache. Scales V 25% more heavily than Q inside
// the reward term: once the quality screen is cleared, affordability matters
// more than marginal quality increments. Walk-forward Sharpe 1.75 (under the
// pre-2026-04-30 formula — backtest needs to be re-run under the corrected
// formula, scope deferred).
// β (Valuation weight coefficient) — controls how much weight V gets
// relative to Q in the Euclidean reward term. β=1.0 is equal-weight,
// β > 1 over-weights V, β < 1 under-weights V.
//
// β=0.90 (slight V discount) + QUALITY_SCALE=1.25
// (Q scaled up). Together: effective Q dominance over V = 1.25/0.90 =
// 1.39×. Walkforward Sharpe peak: 1.21 at this exact (α=1.25, β=0.90)
// corner. Both neighbors (β=0.85 / β=0.95) drop 0.07-0.08 Sharpe —
// it's a knife-edge peak validated by full 0.1-grain sweep across
// β ∈ [0.7, 1.0].
//
// Philosophical framing (Steve 2026-05-15): "Quality is what companies
// DO. Valuation is what the market THINKS. Companies control Q via
// management, capital allocation, operational discipline. No company
// controls its V — that's collective opinion. We scale up the signal
// companies can earn (Q) and discount the signal they cannot (V)."
//
// Why this works empirically: Q is grounded in fundamentals and changes
// slowly (a great company today is almost certainly great in 12 months).
// V is noisy — multiples mean-revert, regimes flip. A model weighted
// toward the slow signal produces more stable, defensible alpha. The
// data confirms: Sharpe 1.21 / drop-top-5% 0.94 at this corner vs
// Sharpe 0.78 / drop-top-5% 0.82 at prior production β=1.25.
export const VALUATION_BETA = 0.90;

// α (Quality scale coefficient) — multiplies Q before the squared
// reward term. α=1.0 is baseline; α>1 over-weights Q vs V.
//
// α=1.25 chosen after walkforward sweep across α ∈ [1.0, 3.0]
// × 12 vintages × 972 grid cells. At α=1.25, β=1.0:
//   oosSharpe 1.17 (vs 0.78 at current production = +0.39, +50%)
//   oosMean   20.9% (vs 24.5% current = -3.6pp)
//   oosStdev  17.9% (vs 31.2% = -13.3pp)
//   p25       0.99 (vs 0.70 = +0.29)
//   Drop-top-5% Sharpe  1.07 (vs 0.82 = +0.25)  ← ABOVE 1.0
//
// The drop-top-5% Sharpe ABOVE 1.0 is the most important finding: it
// means the basket's risk-adjusted edge SURVIVES even when the top 5%
// of names per vintage are removed. The alpha is broadly distributed
// across positions — the strategy doesn't depend on getting lucky on
// any single pick. Steve: "this speaks to my thesis which is q is
// harder to achieve."
//
// Translation: a Q signal is treated as 25% more impactful than a V
// signal of equal z-score magnitude. Q is the dominant axis, V is
// the supporting factor.
export const QUALITY_SCALE = 1.25;

/** Valuation shift — the V inflection point. Currently 0.5, which means the
 *  Euclidean-vs-linear branch flips at V=-0.5 instead of V=0.
 *
 *  Background: with VALUATION_SHIFT=0, a high-Q compounder at modest premium
 *  to peers (V≈-0.5) gets the linear penalty branch even though Q is strong.
 *  The asymmetry-of-magnitudes (penalty at small |V| is 6.6× the reward at
 *  equal magnitude — see Steve analysis 2026-05-14) effectively excluded
 *  AAPL / LRCX / KLAC / FTNT / ASML / IDXX / ADBE / META / NVDA from the
 *  basket — names a Buffett-style PM holds for decades. The model was
 *  Klarman-flavored against a Buffett-flavored portfolio.
 *
 *  Shifting the inflection to V=-0.5 says: we allow up to 0.5σ premium to
 *  peers as "fair price" for quality. Names cheaper than that get geometric
 *  reward; names more expensive than that still get the linear penalty.
 *
 *  Walkforward validation 2026-05-14 (12 vintages, LOOCV across 972-cell grid):
 *    SHIFT     oosMean   oosStdev   Sharpe   p25    DropTop5%-Sharpe   Sensitivity
 *    0.00      23.0%     28.4%      0.81     0.75   0.73                13.3%
 *    0.20      22.6%     26.6%      0.85     0.77   0.69                19.5%
 *    0.35      22.8%     27.2%      0.84     0.78   0.85                10.5%
 *    0.50      24.5%     31.2%      0.78     0.69   0.82                7.5%   ← ADOPTED
 *    0.65      20.7%     27.9%      0.74     0.69   0.65                12.0%
 *    0.80      19.2%     24.8%      0.77     0.70   0.79                16.8%
 *
 *  SHIFT=0.5 maximises oosMean (+1.5pp/yr over baseline). Sharpe -0.03 vs
 *  baseline is well within noise. Drop-top-5% Sharpe IMPROVES (alpha more
 *  broadly distributed). Parameter sensitivity nearly halves (7.5% vs
 *  13.3%) — the chosen winner is operating in a much more stable region.
 *  Steve directive: "Im a hedge fund, manage the down via hedging. More
 *  up without much sacrifice to Sharpe is what I want."
 */
export const VALUATION_SHIFT = 0.5;

export function compositeScore(qualityScore: number | null, valuationScore: number | null): number | null {
  if (qualityScore == null || valuationScore == null) return null;
  // Asymmetric on BOTH axes (Q<0 fix 2026-04-30). Shifted V inflection
  // (V=-0.5) since 2026-05-14. Q-scale = 1.25 since 2026-05-15 — Q
  // is multiplied before the squared reward to emphasise the "Q is
  // harder to achieve" thesis.
  //
  //   q'        = α · Q                        (α = QUALITY_SCALE)
  //   v'        = β · (V + VALUATION_SHIFT)
  //   composite = sqrt(max(q',0)² + max(v',0)²) + min(q',0) + min(v',0)
  const q = QUALITY_SCALE * qualityScore;
  const v = VALUATION_BETA * (valuationScore + VALUATION_SHIFT);
  const qPositive = Math.max(q, 0);
  const qPenalty = Math.min(q, 0);
  const vPositive = Math.max(v, 0);
  const vPenalty = Math.min(v, 0);
  return Math.sqrt(qPositive ** 2 + vPositive ** 2) + qPenalty + vPenalty;
}

/** Production Q axis = blended (0.75 peer-z + 0.25 universe-absolute); falls back
 *  to peer-z for stocks without a blended value. Use this anywhere the UI DISPLAYS
 *  the Q value, positions it on the Q scatter axis, or computes the composite for
 *  display/selection. Do NOT use it in peer-group DIAGNOSTIC surfaces. */
export const prodQuality = (s: { qualityScoreBlended?: number | null; qualityScore?: number | null }): number | null =>
  s.qualityScoreBlended ?? s.qualityScore ?? null;

/** Filter passes if a stock is Alpha-Zone-eligible: composite ≥ BUY tier
 *  (1.5). The composite is the central measure; admission gates on it
 *  directly rather than a separate quality threshold. Replaces the prior
 *  Q-only rule (Q ≥ 0) on 2026-05-14 — the rules now match the published
 *  tier vocabulary (BUY ≥ 1.5). */
export function isAlphaZoneEligible(qualityScore: number | null, valuationScore: number | null): boolean {
  if (qualityScore == null || valuationScore == null) return false;
  const c = compositeScore(qualityScore, valuationScore);
  return c != null && c >= ALPHA_ZONE_COMPOSITE_MIN;
}

// Legacy linear weights (no longer used for primary selection, retained for any callers)
export const QUALITY_WEIGHT = 2 / 3;
export const VALUATION_WEIGHT = 1 / 3;

/**
 * Verbal label for a Valuation z-score.
 *
 * Convention reminder: V > 0 = cheap (good), V < 0 = expensive (bad). This
 * follows the model's "higher z-score = better" rule across all factors. Some
 * investors verbalize valuation the opposite way ("undervalued = -10% gap").
 * To avoid that confusion, every V display in the UI also shows the verbal
 * reading. Thresholds at ±0.5σ for "fair" band.
 *
 * Returns: short word + color matching the rest of the app's tier palette.
 */
export function valuationVerbal(v: number | null | undefined): { word: string; color: string } {
  // Plain language only — Steve's directive 2026-04-30: "cheap expensive
  // high quality or low high composite or low. i speak simple language"
  if (v == null) return { word: '—', color: '#94a3b8' };
  if (v >= 1.0) return { word: 'very cheap', color: '#22c55e' };
  if (v >= 0.5) return { word: 'cheap', color: '#4ade80' };
  if (v >= -0.5) return { word: 'fairly priced', color: '#94a3b8' };
  if (v >= -1.0) return { word: 'expensive', color: '#fb923c' };
  return { word: 'very expensive', color: '#f87171' };
}

/**
 * Verbal label for a Quality z-score. Same direction as V (higher = better)
 * but different thresholds because Q is more spread (deeper tail).
 */
export function qualityVerbal(q: number | null | undefined): { word: string; color: string } {
  if (q == null) return { word: '—', color: '#94a3b8' };
  if (q >= 2) return { word: 'very high quality', color: '#22c55e' };
  if (q >= 1) return { word: 'high quality', color: '#4ade80' };
  if (q >= -0.5) return { word: 'average quality', color: '#94a3b8' };
  if (q >= -1) return { word: 'low quality', color: '#fb923c' };
  return { word: 'very low quality', color: '#f87171' };
}

// `compositeVerbal` was retired 2026-04-30 — the canonical composite-tier
// vocabulary is STRONG BUY / BUY / HOLD / SELL via `lib/composite-tiers.ts`.
// All composite-tier rendering goes through `compositeTier(c).label`.
